import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { EsignApiKeyService } from 'src/app/core/services/esign-api-key.service';
import { EstampApiKeyService } from 'src/app/core/services/estamp-api-key.service';
import {
  ApiKeyPreset,
  ApiKeyProduct,
  apiKeyPresetLabel,
  apiKeyPresetsFor
} from 'src/app/core/helpers/api-key-presets';

export type { ApiKeyProduct };

/** Dropdown value that switches the banner back to the paste-a-key input. */
const MANUAL_KEY_OPTION = '__manual__';

/** Shape shared by {@link EsignApiKeyService} and {@link EstampApiKeyService}. */
interface DemoApiKeyStore {
  readonly apiKey: string;
  readonly hasApiKey: boolean;
  readonly maskedApiKey: string;
  readonly changes: Observable<string>;
  save(apiKey: string): void;
  clear(): void;
}

@Component({
  selector: 'app-api-key-banner',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './api-key-banner.component.html',
  styleUrls: ['./api-key-banner.component.scss']
})
export class ApiKeyBannerComponent implements OnInit, OnDestroy {

  /** Which demo flow this banner holds the key for. */
  @Input() product: ApiKeyProduct = 'esign';

  apiKeyInput = '';
  hasApiKey = false;
  maskedApiKey = '';
  isEditing = false;

  presets: ApiKeyPreset[] = [];

  selectedPresetKey = MANUAL_KEY_OPTION;

  activePresetLabel?: string;

  readonly manualKeyOption = MANUAL_KEY_OPTION;

  private apiKeySubscription?: Subscription;

  constructor(
    private esignApiKeyService: EsignApiKeyService,
    private estampApiKeyService: EstampApiKeyService
  ) { }

  ngOnInit(): void {
    this.presets = apiKeyPresetsFor(this.product);

    this.apiKeySubscription = this.store.changes
      .subscribe(apiKey => {
        this.hasApiKey = this.store.hasApiKey;
        this.maskedApiKey = this.store.maskedApiKey;
        this.activePresetLabel = apiKeyPresetLabel(this.product, apiKey);
        this.selectedPresetKey =
          this.activePresetLabel ? apiKey.trim() : MANUAL_KEY_OPTION;
      });
  }

  ngOnDestroy(): void {
    this.apiKeySubscription?.unsubscribe();
  }

  get showForm(): boolean {
    return !this.hasApiKey || this.isEditing;
  }

  get hasPresets(): boolean {
    return this.presets.length > 0;
  }

  get showManualInput(): boolean {
    return !this.hasPresets || this.selectedPresetKey === MANUAL_KEY_OPTION;
  }

  get productLabel(): string {
    return this.product === 'estamp' ? 'eStamp' : 'eSign';
  }

  startEditing(): void {
    this.apiKeyInput = this.activePresetLabel ? '' : this.store.apiKey;
    this.isEditing = true;
  }

  onPresetSelected(selectedValue: string): void {
    this.selectedPresetKey = selectedValue;

    if (selectedValue === MANUAL_KEY_OPTION) {
      this.apiKeyInput = '';
      return;
    }

    this.apiKeyInput = '';
    this.store.save(selectedValue);
    this.isEditing = false;
  }

  saveApiKey(): void {
    if (!this.apiKeyInput?.trim()) {
      return;
    }

    this.store.save(this.apiKeyInput);
    this.apiKeyInput = '';
    this.isEditing = false;
  }

  cancelEditing(): void {
    this.apiKeyInput = '';
    this.isEditing = false;
    this.selectedPresetKey =
      this.activePresetLabel ? this.store.apiKey.trim() : MANUAL_KEY_OPTION;
  }

  clearApiKey(): void {
    this.store.clear();
    this.apiKeyInput = '';
    this.isEditing = false;
    this.selectedPresetKey = MANUAL_KEY_OPTION;
  }

  private get store(): DemoApiKeyStore {
    return this.product === 'estamp'
      ? this.estampApiKeyService
      : this.esignApiKeyService;
  }
}
